// achievementsPanel.ts — the ACHIEVEMENTS wall (opened from the Stats panel). A scrollable 2-column
// grid of milestone cards derived entirely from the existing meta layer (stats.ts firsts + lifetime
// stats + mastery facts) — no Core, no network, no new persisted state. Each card reads as unlocked
// (accent-tinted icon disc + accent border + "Unlocked") or locked (mute lock disc + faded card +
// progress hint), so the state is legible without relying on colour alone (colorblind double-coding).
// Pure UI over the shared full-bleed modal + kit; matches the Tactical OLED Arcade redline.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { modal } from './ui';
import { ctaButton, progressBar, labelText } from './ui/kit';
import { icon, type IconName } from './icons';
import { scaledFont, motionAllowed } from './settings';
import { getStats, getProgress } from './stats';
import { factsLearnedTotal } from './mastery';

/** A single achievement: a measurable milestone over the existing meta stats. */
interface Achievement {
  name: string;
  /** short goal label shown under the name (doubles as the locked-state hint). */
  goal: string;
  icon: IconName;
  /** accent colour when unlocked (border + icon disc). */
  accent: number;
  /** current progress + the target that unlocks it (unlocked when cur >= target). */
  cur: number;
  target: number;
}

/** Build the achievement list from the live meta layer (stats firsts + lifetime + mastery facts). */
function buildAchievements(): Achievement[] {
  const s = getStats();
  const facts = factsLearnedTotal();
  const topics = Object.keys(s.topics).length;
  const rank = getProgress(s).rank; // 0..5 (ROOKIE..MASTER)
  return [
    { name: 'First Win', goal: 'Win a board', icon: 'trophy', accent: PALETTE.gold, cur: s.wins, target: 1 },
    {
      name: 'Threadmaster',
      goal: 'Thread a Gauntlet',
      icon: 'sparkle',
      accent: PALETTE.armed,
      cur: s.firsts.firstThread ? 1 : 0,
      target: 1,
    },
    { name: 'Hot Streak', goal: '3 wins in a row', icon: 'flame', accent: PALETTE.armed, cur: s.bestWinStreak, target: 3 },
    { name: '7-Day Streak', goal: '7-day daily streak', icon: 'flame', accent: PALETTE.gold, cur: s.bestDailyStreak, target: 7 },
    { name: 'Combo Breaker', goal: '8x combo', icon: 'bolt', accent: PALETTE.armed, cur: s.bestCombo, target: 8 },
    { name: 'Sharpshooter', goal: '3,000 in a board', icon: 'star', accent: PALETTE.gold, cur: s.bestScore, target: 3000 },
    { name: 'Polymath', goal: '10 topics played', icon: 'target', accent: PALETTE.scan, cur: topics, target: 10 },
    { name: 'Veteran', goal: '25 boards played', icon: 'gauge', accent: PALETTE.player, cur: s.gamesPlayed, target: 25 },
    { name: 'Champion', goal: '20 boards won', icon: 'crown', accent: PALETTE.gold, cur: s.wins, target: 20 },
    { name: 'Centurion', goal: '100 boards played', icon: 'medal', accent: PALETTE.player, cur: s.gamesPlayed, target: 100 },
    { name: 'Quick Study', goal: '25 facts learned', icon: 'chart', accent: PALETTE.scan, cur: facts, target: 25 },
    { name: 'Scholar', goal: '100 facts learned', icon: 'chart', accent: PALETTE.gold, cur: facts, target: 100 },
    { name: 'Ascendant', goal: 'Reach PRO rank', icon: 'crown', accent: PALETTE.gold, cur: rank, target: 2 },
  ];
}

/** The status line shown under a card name: "Unlocked" when done, else the goal + progress. */
function statusLine(a: Achievement, unlocked: boolean): string {
  if (unlocked) return 'Unlocked';
  // Count-based goals (target > 1) read like the redline: "10 topics played · 7/10". Binary goals
  // (target 1) just show their requirement label.
  return a.target > 1 ? `${a.goal} · ${Math.min(a.cur, a.target)}/${a.target}` : a.goal;
}

export function openAchievements(scene: Phaser.Scene): ReturnType<typeof modal> {
  const m = modal(scene, { title: 'ACHIEVEMENTS', fullBleed: true, railGrad: [PALETTE.gold, PALETTE.armed] });
  const { card, cardW, cardH, bodyTop, footerY } = m;

  const achievements = buildAchievements();
  const unlockedCount = achievements.filter((a) => a.cur >= a.target).length;
  const wide = cardW >= 900;
  const panelW = cardW - 52;

  // — header: "X / N" unlocked count (gold), right-aligned beside the ✕ close —
  const countFs = Math.round(scaledFont(16, 13));
  const count = scene.add
    .text(cardW / 2 - 56, -cardH / 2 + 26, `${unlockedCount} / ${achievements.length}`, {
      fontFamily: FONT.display,
      fontSize: `${countFs}px`,
      color: hex(PALETTE.gold),
      fontStyle: 'bold',
    })
    .setOrigin(1, 0.5);
  card.add(count);

  // — compact progression summary under the header divider —
  const summaryY = bodyTop + 12;
  card.add(labelText(scene, -panelW / 2, summaryY - 12, 'Milestone wall', { size: 11, color: PALETTE.gold }));
  const bar = progressBar(scene, -panelW / 2, summaryY + 12, panelW, 7, unlockedCount / achievements.length, PALETTE.gold);
  card.add(bar.container);

  // — scroll region (between the bar and the DONE CTA) —
  const top = summaryY + 34;
  const doneY = footerY;
  const bottom = doneY - 84;
  const regionH = bottom - top;
  const pad = 22;
  const gap = wide ? 16 : 12;
  const cols = wide ? 3 : 2;
  const innerW = cardW - pad * 2;
  const tileW = (innerW - gap * (cols - 1)) / cols;
  const tileH = wide ? 96 : 118;
  const rowGap = wide ? 10 : 12;

  // Content lives in its own container so it can be scrolled under a fixed mask when it overflows.
  const content = scene.add.container(0, top);
  card.add(content);

  achievements.forEach((a, i) => {
    const col = i % cols;
    const rowIdx = Math.floor(i / cols);
    const tx = -innerW / 2 + tileW / 2 + col * (tileW + gap);
    const ty = tileH / 2 + rowIdx * (tileH + rowGap);
    content.add(buildCard(scene, a, tx, ty, tileW, tileH));
  });

  const rows = Math.ceil(achievements.length / cols);
  const contentH = rows * tileH + (rows - 1) * rowGap;
  const maxScroll = Math.max(0, contentH - regionH);

  if (maxScroll > 0) {
    // Clip the content to the region with a fixed world-space geometry mask. The card is top-anchored
    // (fullBleed), so the mask is derived from the card's live world transform, not screen-centre.
    const worldX = card.x - innerW / 2;
    const worldY = card.y + top;
    const maskG = scene.add.graphics().setVisible(false);
    maskG.fillStyle(0xffffff, 1);
    maskG.fillRect(worldX, worldY, innerW, regionH);
    m.layer.add(maskG);
    content.setMask(maskG.createGeometryMask());

    let scroll = 0;
    const apply = (): void => {
      scroll = Phaser.Math.Clamp(scroll, 0, maxScroll);
      content.y = top - scroll;
    };

    // A thin scrollbar thumb on the right edge so overflow is discoverable.
    const trackX = cardW / 2 - 10;
    const thumbH = Math.max(28, regionH * (regionH / contentH));
    const thumb = scene.add.graphics();
    const paintThumb = (): void => {
      const t = maxScroll > 0 ? scroll / maxScroll : 0;
      const ty = top + t * (regionH - thumbH);
      thumb.clear();
      thumb.fillStyle(PALETTE.lineStrong, 0.9);
      thumb.fillRoundedRect(trackX - 2, ty, 4, thumbH, 2);
    };
    card.add(thumb);
    paintThumb();

    // Drag-to-scroll (transparent catcher over the region) + wheel.
    let downY = 0;
    let startScroll = 0;
    let dragging = false;
    const catcher = scene.add
      .rectangle(0, top + regionH / 2, innerW, regionH, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: false });
    card.add(catcher);
    catcher.on('pointerdown', (p: Phaser.Input.Pointer) => {
      dragging = true;
      downY = p.y;
      startScroll = scroll;
    });
    // Named handlers so teardown removes ONLY ours (never the host scene's input listeners).
    const onMove = (p: Phaser.Input.Pointer): void => {
      if (!dragging || !p.isDown) return;
      scroll = startScroll + (downY - p.y);
      apply();
      paintThumb();
    };
    const onUp = (): void => {
      dragging = false;
    };
    const onWheel = (_p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number): void => {
      scroll += dy * 0.5;
      apply();
      paintThumb();
    };
    scene.input.on('pointermove', onMove);
    scene.input.on('pointerup', onUp);
    scene.input.on('wheel', onWheel);
    // Tear down only our global input listeners with the modal so nothing leaks across panels.
    m.layer.once(Phaser.GameObjects.Events.DESTROY, () => {
      scene.input.off('pointermove', onMove);
      scene.input.off('pointerup', onUp);
      scene.input.off('wheel', onWheel);
    });
  }

  // — DONE (primary green CTA) —
  card.add(ctaButton(scene, 0, doneY, 'Done', () => m.close(), { width: cardW - 44, height: 56 }));

  return m;
}

/** Build one achievement card (icon disc + name + status line, with locked/unlocked styling). */
function buildCard(
  scene: Phaser.Scene,
  a: Achievement,
  x: number,
  y: number,
  w: number,
  h: number,
): Phaser.GameObjects.Container {
  const c = scene.add.container(x, y);
  const unlocked = a.cur >= a.target;
  const compact = w < 230;
  const progress = a.target > 0 ? Math.min(a.cur, a.target) / a.target : 0;

  // Card body — unlocked: raised surface2 + accent@0.4 border; locked: tile fill + hairline border.
  const bg = scene.add.graphics();
  bg.fillStyle(unlocked ? PALETTE.surface2 : PALETTE.tile, unlocked ? 1 : 0.82);
  bg.fillRoundedRect(-w / 2, -h / 2, w, h, 12);
  bg.lineStyle(1.5, unlocked ? a.accent : PALETTE.lineStrong, unlocked ? 0.8 : 0.55);
  bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 12);
  c.add(bg);

  // Icon disc: left-aligned on desktop cards, centered on narrow phone cards.
  const discX = compact ? 0 : -w / 2 + 38;
  const discY = compact ? -h / 2 + 30 : -8;
  const discR = compact ? 18 : 20;
  const disc = scene.add.graphics();
  disc.fillStyle(unlocked ? a.accent : PALETTE.lineStrong, unlocked ? 0.18 : 0.32);
  disc.fillCircle(discX, discY, discR);
  c.add(disc);
  c.add(icon(scene, unlocked ? a.icon : 'lock', discX, discY, compact ? 20 : 22, unlocked ? a.accent : PALETTE.lockGlyph));

  // Name + status line. Locked text stays readable; the lock glyph/progress bar carry the state.
  const textX = compact ? 0 : -w / 2 + 72;
  const nameY = compact ? h / 2 - 52 : -24;
  const statusY = compact ? h / 2 - 29 : -1;
  const textOrigin = compact ? 0.5 : 0;
  const name = scene.add
    .text(textX, nameY, a.name, {
      fontFamily: FONT.display,
      fontSize: `${scaledFont(16, 13)}px`,
      color: hex(PALETTE.text),
      fontStyle: 'bold',
      align: compact ? 'center' : 'left',
      wordWrap: { width: compact ? w - 20 : w - 98 },
    })
    .setOrigin(textOrigin, 0.5);
  const status = scene.add
    .text(textX, statusY, statusLine(a, unlocked), {
      fontFamily: FONT.family,
      fontSize: `${scaledFont(12, 11)}px`,
      color: hex(unlocked ? PALETTE.correct : PALETTE.textDim),
      align: compact ? 'center' : 'left',
      wordWrap: { width: compact ? w - 18 : w - 104 },
    })
    .setOrigin(textOrigin, 0.5);
  c.add([name, status]);

  const barW = compact ? w - 36 : w - 104;
  const barX = compact ? -barW / 2 : textX;
  const barY = compact ? h / 2 - 12 : 30;
  const prog = scene.add.graphics();
  prog.fillStyle(PALETTE.lineStrong, 0.58);
  prog.fillRoundedRect(barX, barY - 2, barW, 4, 2);
  if (unlocked || progress > 0) {
    prog.fillStyle(unlocked ? a.accent : PALETTE.scan, unlocked ? 0.95 : 0.85);
    prog.fillRoundedRect(barX, barY - 2, Math.max(4, barW * (unlocked ? 1 : progress)), 4, 2);
  }
  c.add(prog);

  // Unlocked cards get a calm one-shot fade-in so the wall isn't static without looping motion.
  if (unlocked && motionAllowed()) {
    c.setAlpha(0);
    scene.tweens.add({ targets: c, alpha: 1, duration: 160, delay: 20 });
  }

  return c;
}
